import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { BaseModal } from "../../../../../common/components/BaseModal/BaseModal";
import { colors } from '../../../../../common/styles/theme.styles';
import { useStore } from "../../../../app/data/root.store";
import { SmartTagIcon } from "../../VaultMetadataPanel/MetadataLabelsSection/TagsTagCategory/SmartTagIcon";
import { AddCustomTagButton } from "../../VaultMetadataPanel/MetadataLabelsSection/TagsTagCategory/TagEditorModal.styles";
import { VaultActionButton, VaultModalContent, VaultModalFooter, VaultModalHeader, VaultWarningMessage } from './SharedModalComponents';
import {
    AvailableTagsContainer,
    BulkTagEditorContainer,
    CreateTagInput,
    CreateTagInputContainer,
    PlusIcon,
    SelectedTagsContainer,
    TagItem,
    TagSection,
    TagSectionTitle
} from './TagsEditModal.styles';
import { PREDEFINED_TAG_NAMES } from "../../../data/vault-store";

const BulkTagEditor = ({ selectedTags, onTagsChange }) => {
    const { vaultStore } = useStore();
    const [newTagInput, setNewTagInput] = useState('');

    useEffect(() => {
        if (vaultStore.userTags.length === 0) {
            vaultStore.fetchUserTags();
        }
    }, [vaultStore]);

    const handleTagToggle = (tag) => {
        const isSelected = selectedTags.some(t => t.id === tag.id);
        if (isSelected) {
            onTagsChange(selectedTags.filter(t => t.id !== tag.id));
        } else {
            onTagsChange([...selectedTags, tag]);
        }
    };

    const handleCreateTag = () => {
        const trimmedInput = newTagInput.trim();
        if (trimmedInput) {
            
            const customTag = {
                id: trimmedInput.toLowerCase(),
                label: trimmedInput,
                isCustom: true
            };

            const isAlreadySelected = selectedTags.some(tag =>
                tag.id.toLowerCase() === customTag.id.toLowerCase()
            );

            if (!isAlreadySelected) {
                onTagsChange([customTag, ...selectedTags]);
            }

            setNewTagInput('');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && newTagInput.trim()) {
            handleCreateTag();
        }
    };

    return (
        <BulkTagEditorContainer>
            <TagSection>
                <TagSectionTitle>
                    Create new tag
                </TagSectionTitle>
                <CreateTagInputContainer>
                    {
                        ("" === newTagInput.trim()) &&
                        <PlusIcon>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path d="M12 5V19M5 12H19" stroke="#1A1B1D" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </PlusIcon>
                    }
                    <CreateTagInput
                        type="text"
                        placeholder="Create new type tag"
                        value={newTagInput}
                        autoFocus
                        onChange={(e) => setNewTagInput(e.target.value?.trim())}
                        onKeyPress={handleKeyPress}
                        maxLength={48}
                    />
                    {newTagInput.trim() && (
                        <AddCustomTagButton onClick={handleCreateTag}>
                            Add new tag
                        </AddCustomTagButton>
                    )}
                </CreateTagInputContainer>
            </TagSection>

            {selectedTags.length > 0 && (
                <TagSection>
                    <TagSectionTitle>
                        Selected ({selectedTags.length})
                    </TagSectionTitle>
                    <SelectedTagsContainer>
                        {selectedTags.map((tag) => (
                            <TagItem
                                key={tag.id}
                                onClick={() => handleTagToggle(tag)}
                                isSelected={true}
                            >
                                <SmartTagIcon tagName={tag.id} size={24} />
                                <span>{PREDEFINED_TAG_NAMES[tag.id] || tag.label || tag.name}</span>
                                <span style={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 12L10.5 15.5L17 9" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </TagItem>
                        ))}
                    </SelectedTagsContainer>
                </TagSection>
            )}

            <TagSection>
                <TagSectionTitle>
                    Available tags
                </TagSectionTitle>
                <AvailableTagsContainer>
                    {vaultStore.allAvailableTags
                        .filter(tag => !selectedTags.some(selectedTag => selectedTag.id === tag.id))
                        .map((tag) => (
                            <TagItem
                                key={tag.id}
                                onClick={() => handleTagToggle(tag)}
                                isSelected={false}
                            >
                                <SmartTagIcon tagName={tag.id} size={24} />
                                <span>{PREDEFINED_TAG_NAMES[tag.id] || tag.label || tag.name}</span>
                            </TagItem>
                        ))}
                </AvailableTagsContainer>
            </TagSection>
        </BulkTagEditorContainer>
    );
};

export const TagsEditModal = observer(({ onClose, onSave }) => {
    const { vaultStore } = useStore();
    const [selectedTags, setSelectedTags] = useState([]);

    useEffect(() => {
        if (vaultStore.selectedFilesForBulkEdit.length > 0) {
            const commonTags = vaultStore.commonTagsAcrossSelectedFiles;

            const tagObjects = commonTags.map(tagName => {
                return vaultStore.userTags.find(option =>
                    option.label?.toLowerCase() === tagName.toLowerCase() ||
                    option.id?.toLowerCase() === tagName.toLowerCase()
                ) || { id: tagName, label: tagName };
            }).filter(Boolean);

            setSelectedTags(tagObjects);
        }
    }, [vaultStore.selectedFilesForBulkEdit, vaultStore.userTags, vaultStore.commonTagsAcrossSelectedFiles]);

    const handleSave = () => {
        const tagNames = selectedTags.map(tag => tag.id || tag.tag || tag.label);
        onSave(tagNames);
    };

    const getModalTitle = () => {
        const count = vaultStore.selectedFilesForBulkEdit.length;
        return `Edit selected item${count === 1 ? '' : 's'} (${count})`;
    };

    return (
        <BaseModal width="600px" borderRadius={12} onBackDropClick={onClose}>
            <VaultModalHeader onClose={onClose}>
                {getModalTitle()}
            </VaultModalHeader>

            <VaultModalContent>
                <BulkTagEditor
                    selectedTags={selectedTags}
                    onTagsChange={setSelectedTags}
                />

                <VaultWarningMessage>
                    This action will replace all existing tags with the selected tags for all selected files.
                </VaultWarningMessage>
            </VaultModalContent>

            <VaultModalFooter>
                <VaultActionButton
                    onClick={handleSave}
                    backgroundColor={colors.darkGray1}
                    whiteText
                >
                    Save
                </VaultActionButton>
            </VaultModalFooter>
        </BaseModal>
    );
}); 